import "./NotificationToggleComponent.scss";
import {IComponentOptions, IOnDestroy, IOnInit, IScope} from "angular";
import {ReplaySubject, Scheduler, Subject} from "rxjs";
import {UserNotificationPreferencesService} from "../../services/preferences/UserNotificationPreferencesService";
import {MessageType} from "../../services/preferences/models/MessageType";
import {NotificationState} from "../../services/preferences/models/NotificationState";
import {UserNotificationPreferences} from "../../services/preferences/preferences/UserNotificationPreferences";

export const NotificationToggleComponent = "tfNotificationTypeToggle";

export function notificationToggleComponent(parentDir: string): IComponentOptions {
    return {
        templateUrl: `${parentDir}/enable-notifications/NotificationToggleComponent.html`,
        controllerAs: "ctrl",
        controller: NotificationComponentController,
        bindings: {
            messageType: "<"
        }
    };
}

export class NotificationComponentController implements IOnInit, IOnDestroy {

    public messageType: MessageType;
    public notificationsAvailable = false;
    public notificationsAllowed = false;
    public notificationsBlocked = false;
    public messageNotificationsEnabled = false;
    private updatePreferences$ = new Subject<NotificationState>();
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private $scope: IScope,
                       private userNotificationPreferencesService: UserNotificationPreferencesService) {
    }

    public $onInit(): void {
        this.watchEnabledState();
        this.watchAvailableState();
        this.watchAllowedState();
        this.watchBlockedState();
        this.watchUpdatePreferences();
    }

    private watchEnabledState() {
        this.userNotificationPreferencesService
            .preferences
            .takeUntil(this.destroyed$)
            .map((pref) => pref.state(this.messageType))
            .map((state) => state === NotificationState.ACTIVE)
            .subscribe((enabled) => {
                this.$scope.$applyAsync(() => this.messageNotificationsEnabled = enabled);
            });
    }

    private watchAvailableState() {
        this.userNotificationPreferencesService
            .available
            .takeUntil(this.destroyed$)
            .subscribe((available) => {
                this.$scope.$applyAsync(() => this.notificationsAvailable = available);
            });
    }

    private watchAllowedState() {
        this.userNotificationPreferencesService
            .enabled
            .takeUntil(this.destroyed$)
            .subscribe((allowed) => {
                this.$scope.$applyAsync(() => this.notificationsAllowed = allowed);
            });
    }

    private watchBlockedState() {
        this.userNotificationPreferencesService.blocked.takeUntil(this.destroyed$)
            .subscribe((blocked) => {
                this.$scope.$applyAsync(() => this.notificationsBlocked = blocked);
            });
    }

    private watchUpdatePreferences() {
        this.updatePreferences$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .withLatestFrom(this.userNotificationPreferencesService.preferences)
            .subscribe(([state, pref]) => this.updatePreference(state, pref));
    }

    private updatePreference(state: NotificationState, current: UserNotificationPreferences) {
        const updated = current.update(this.messageType, state);
        this.userNotificationPreferencesService.update(updated);
    }

    public enableNotifications() {
        this.updatePreferences$.next(NotificationState.ACTIVE);
        if (!this.notificationsBlocked) {
            this.userNotificationPreferencesService.subscribe();
        }
    }

    public disableNotifications() {
        this.updatePreferences$.next(NotificationState.DISABLED);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
